import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import { Select, MenuItem } from '@mui/material';

import { IoClose } from "react-icons/io5";
import { LiaUniversitySolid } from "react-icons/lia";
import { MdLocalPhone } from "react-icons/md";
import { HiMiniUsers } from "react-icons/hi2";
import { LuBuilding } from "react-icons/lu";

import SoftTypography from "../../components/SoftTypography";
import SoftButton from "../../components/SoftButton";

import PropTypes from "prop-types";
import { getUniversities, updateDepartment } from "../../axios-client";
import { LIBRARY_ADMIN_ROLE } from "../../utlis/constant";
import { useSoftUIController } from "../../context";

import { useTranslation } from "react-i18next";
import "./assets/departmentsStyleSheet.css";

const EditDepartmentDialog = ({ open, onClose, onDepartmentAdded, departmentData, setSnackbar }) => {
    const { t, i18n } = useTranslation("departments");
    const isRTL = i18n.dir() === 'rtl';
    const [formData, setFormData] = useState({
        name: departmentData?.name || '',
        university: departmentData?.university.id || '',
        phone: departmentData?.phone || '',
        max_users: departmentData?.max_users || '',
    });
    const [errors, setErrors] = useState({});
    const [universities, setUniversities] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const [controller] = useSoftUIController();
    const { role } = controller;

    useEffect(() => {
        if (role && open && departmentData) {
            setFormData({
                name: departmentData.name,
                university: departmentData.university.id,
                phone: departmentData.phone,
                max_users: departmentData.max_users,
            });
        }
    }, [open, departmentData, role]);

    useEffect(() => {
        async function loadUniversities() {
            try {
                const response = await getUniversities();
                setUniversities(response.data.universities || []);
            } catch (error) {
                console.error("Failed to load universities", error);
                setUniversities([]);
            }
        }
        loadUniversities();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const validateForm = () => {
        let formErrors = {};
        if (!formData.name) formErrors.name = t("validation.department_required");
        if (!formData.university) formErrors.university = t("validation.university_required");
        return formErrors;
    };

    const handleSubmit = async () => {
        const formErrors = validateForm();
        if (Object.keys(formErrors).length === 0) {
            try {
                await updateDepartment(departmentData.id, formData);
                onDepartmentAdded();
                setSnackbar({ show: true, snackbarMessage: t("notification.edit_success"), severity: 'success' });
                onClose();
            } catch (error) {
                setSnackbar({ show: true, snackbarMessage: t("notification.edit_error"), severity: 'error' });
            }
        } else {
            setErrors(formErrors);
            setSnackbar({ show: true, snackbarMessage: t("validation.fill_required"), severity: 'error' });
        }
    };

    const handleCancel = () => {
        onClose();
    };

    return (
        <Dialog open={open} maxWidth="sm" fullWidth>
            <DialogTitle
                id="form-dialog-title"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: isRTL ? '1.3rem' : '1.5rem',
                    fontFamily: isRTL ? "'Tajawal', Arial, sans-serif" : "'Roboto', Arial, sans-serif",
                }}
            >
                {t("dialog.edit_title")}
                <IoClose
                    aria-label="close"
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        [isRTL ? 'left' : 'right']: 16,
                        top: 16,
                        cursor: 'pointer'
                    }}
                />
            </DialogTitle>

            <Divider />
            <DialogContent>
                <label htmlFor="name" className="label-style">{t("table_headers.department_name")}</label>
                <div style={inputContainerStyle}>
                    <LuBuilding className="icon-style" />
                    <input
                        type="text"
                        id="name"
                        name="name"
                        style={inputStyle}
                        value={formData.name}
                        onChange={handleChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                    />
                </div>
                {errors.name && <p style={{ color: 'red', fontSize: "12px" }}>{errors.name}</p>}

                <label htmlFor="university" className="label-style">{t("table_headers.university")}</label>
                <div style={inputContainerStyle}>
                    <LiaUniversitySolid className="icon-style" />
                    {role === LIBRARY_ADMIN_ROLE ? (
                        universities.map((university) => (
                            <input
                                type="text"
                                id="university"
                                name="university"
                                style={inputStyle}
                                key={university.id}
                                value={university.name}
                                disabled
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => setIsFocused(false)}
                            />
                        ))
                    ) : (
                        <Select
                            id="university"
                            name="university"
                            value={formData.university}
                            onChange={handleChange}
                            style={inputStyle}
                            displayEmpty
                            fullWidth
                        >
                            <MenuItem disabled value="">{t("dialog.select_university")}</MenuItem>
                            {universities.map((university) => (
                                <MenuItem key={university.id} value={university.id}>
                                    {university.name}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                </div>
                {errors.university && (
                    <SoftTypography color="error" fontSize="small">
                        {errors.university}
                    </SoftTypography>
                )}

                <label htmlFor="phone" className="label-style">{t("table_headers.phone")}</label>
                <div style={inputContainerStyle}>
                    <MdLocalPhone className="icon-style" />
                    <input
                        type="text"
                        id="phone"
                        name="phone"
                        style={inputStyle}
                        value={formData.phone}
                        onChange={handleChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                    />
                </div>

                <label htmlFor="max_users" className="label-style">{t("table_headers.max_users")}</label>
                <div style={inputContainerStyle}>
                    <HiMiniUsers className="icon-style" />
                    <input
                        type="number"
                        id="max_users"
                        name="max_users"
                        style={inputStyle}
                        value={formData.max_users}
                        onChange={handleChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                    />
                </div>
            </DialogContent>
            <DialogActions>
                <SoftButton variant="outlined" color="dark" onClick={handleCancel}>
                    {t("button.cancel")}
                </SoftButton>
                <SoftButton variant="gradient" color="primary" onClick={handleSubmit} sx={{ml: 1, mr:  (isRTL ? 1 : 0)}}>
                    {t("button.save")}
                </SoftButton>
            </DialogActions>
        </Dialog>
    );
};

const inputContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid #dcdfe3`,
    borderRadius: '8px',
    padding: '5px 10px',
    marginBottom: '10px',
    transition: 'border-color 0.3s ease',
};

const inputStyle = {
    width: '100%',
    padding: '10px 0px',
    border: 'none',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'Arial, Roboto',
};

EditDepartmentDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onDepartmentAdded: PropTypes.func,
    setSnackbar: PropTypes.func,
    departmentData: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        max_users: PropTypes.number,
        phone: PropTypes.string,
        university: PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
        }),
    }),
};

export default EditDepartmentDialog;
